import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FLIGHT_DEALS_ONLY } from "@/lib/release";
import FlightLaunchProfile from "@/pages/FlightLaunchProfile";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AccountSettings from "@/pages/AccountSettings";
import ResetPassword from "@/pages/ResetPassword";
import VerifyContact from "@/pages/VerifyContact";
import VerifyEmail from "@/pages/VerifyEmail";
import Landing from "@/pages/LandingEntry";
import Trips from "@/pages/Trips";
import TripWorkspace from "@/pages/TripWorkspace";
import TripOperationsPage from "@/pages/TripOperationsPage";
import TripPublicPreview from "@/pages/TripPublicPreview";
import AiPlanner from "@/pages/AiPlanner";
import InviteAccept from "@/pages/InviteAccept";
import MyTrips from "@/pages/MyTrips";
import ParticipantTravelHub from "@/pages/ParticipantTravelHub";
import ParticipantPrivacySettings from "@/pages/ParticipantPrivacySettings";
import OrganizationSettings from "@/pages/OrganizationSettings";
import PublicTripDiscovery from "@/pages/PublicTripDiscovery";
import PublicTripDetail from "@/pages/PublicTripDetail";
import PrefetchedDeals from "@/pages/PrefetchedDeals";
import FlightFareCalendar from "@/pages/FlightFareCalendar";
import Feedback from "@/pages/Feedback";
import ManageSubscription from "@/pages/ManageSubscription";
import AppLayout from "@/components/AppLayout";
import TravellerLayout from "@/components/traveller/TravellerLayout";
import TravellerTripRoute from "@/components/traveller/TravellerTripRoute";
import TravellerWorkspace from "@/pages/TravellerWorkspace";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return <div className="flex min-h-screen items-center justify-center bg-[#F7F6F2]"><Loader2 className="h-6 w-6 animate-spin text-[#2D7A65]" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (FLIGHT_DEALS_ONLY && user.account_type !== "traveller") return <Navigate to="/login" replace />;
  return children;
}

function HomeRedirect() {
  const { home } = useAuth();
  return <Navigate to={home} replace />;
}

function TravellerOnly({ children }) {
  const { user, home } = useAuth();
  return user?.account_type === "traveller" ? children : <Navigate to={home} replace />;
}

function ParticipantRoute({ children, travellerPath }) {
  const { user } = useAuth();
  return user?.account_type === "traveller" ? <Navigate to={travellerPath} replace /> : children;
}

function DiscoveryRoute() {
  const { user } = useAuth();
  return user?.account_type === "traveller" ? <TravellerLayout><PublicTripDiscovery /></TravellerLayout> : <PublicTripDiscovery />;
}

function Root() {
  const { user } = useAuth();
  if (user === null)
    return <div className="flex min-h-screen items-center justify-center bg-[#0E1512]"><Loader2 className="h-6 w-6 animate-spin text-[#3FA98C]" /></div>;
  if (!user) return <Landing />;
  return <HomeRedirect />;
}

function FlightDealsRoute({ prefetched = false }) {
  const { user } = useAuth();
  if (user === null)
    return <div className="flex min-h-screen items-center justify-center bg-[#0E1512]"><Loader2 className="h-6 w-6 animate-spin text-[#3FA98C]" /></div>;
  const content = prefetched ? <PrefetchedDeals /> : <FlightFareCalendar embedded />;
  if (user?.account_type === "traveller") return <TravellerLayout>{content}</TravellerLayout>;
  if (FLIGHT_DEALS_ONLY && user) return <Navigate to="/login" replace />;
  if (user) return <AppLayout>{content}</AppLayout>;
  return <Navigate to={prefetched ? `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}` : "/login?next=%2Fflight-deals"} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Future routes are preserved below and paused during the Flight Deals launch. */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/account/settings" element={<Protected><AccountSettings /></Protected>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-contact" element={<Protected><VerifyContact /></Protected>} />
            {!FLIGHT_DEALS_ONLY && <Route path="/invite/:token" element={<InviteAccept />} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/discover" element={<DiscoveryRoute />} />}
            <Route path="/flight-deals" element={<FlightDealsRoute />} />
            <Route path="/prefetched-deals" element={<FlightDealsRoute prefetched />} />
            {!FLIGHT_DEALS_ONLY && <Route path="/feedback" element={<Feedback />} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/reviews" element={<Feedback review />} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/discover/:tripId" element={<PublicTripDetail />} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/public/trips/:tripId" element={<PublicTripDetail />} />}
            <Route path="/landing" element={<Landing />} />
            <Route path="/" element={<Root />} />
            {!FLIGHT_DEALS_ONLY && <Route path="/trips" element={<Protected><Trips /></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/trips/:id/public-preview" element={<Protected><TripPublicPreview /></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/trips/:id/participants" element={<Protected><TripOperationsPage section="participants" /></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/trips/:id/bookings" element={<Protected><TripOperationsPage section="bookings" /></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/trips/:id/finance" element={<Protected><TripOperationsPage section="finance" /></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/trips/:id/documents" element={<Protected><TripOperationsPage section="documents" /></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/trips/:id" element={<Protected><TripWorkspace /></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/ai/plan" element={<Protected><AiPlanner /></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/traveller" element={<Protected><TravellerOnly><TravellerWorkspace /></TravellerOnly></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/traveller/trips" element={<Protected><TravellerOnly><TravellerWorkspace section="trips" /></TravellerOnly></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/traveller/wishlist" element={<Protected><TravellerOnly><TravellerWorkspace section="wishlist" /></TravellerOnly></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/traveller/memory-map" element={<Protected><TravellerOnly><TravellerWorkspace section="memory-map" /></TravellerOnly></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/traveller/memories" element={<Protected><TravellerOnly><TravellerWorkspace section="memories" /></TravellerOnly></Protected>} />}
            <Route path="/traveller/profile" element={<Protected><TravellerOnly>{FLIGHT_DEALS_ONLY ? <FlightLaunchProfile /> : <TravellerWorkspace section="profile" />}</TravellerOnly></Protected>} />
            {!FLIGHT_DEALS_ONLY && <Route path="/my" element={<Protected><ParticipantRoute travellerPath="/traveller/trips"><MyTrips /></ParticipantRoute></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/my/hub" element={<Protected><ParticipantRoute travellerPath="/traveller"><ParticipantTravelHub /></ParticipantRoute></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/my/privacy" element={<Protected><ParticipantRoute travellerPath="/traveller/profile"><ParticipantPrivacySettings /></ParticipantRoute></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/my/:tripId" element={<Protected><TravellerTripRoute /></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/organization/settings" element={<Protected><OrganizationSettings /></Protected>} />}
            {!FLIGHT_DEALS_ONLY && <Route path="/subscription" element={<Protected><ManageSubscription /></Protected>} />}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
